import { IElement, IFontFamily, IStorePack, IUpload, TemplateDetails } from '@/interfaces/editor'
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import store from '@/store/store'
import { UserInfoStorage } from '@/utils/userInfoStorage'
import { syncOneSignalUser } from '@/libs/onesignal'
import { parseSubscriptionStore, SubscriptionStore } from './subscriptionStore'
import { getSafetyIdentifier } from '@/utils/safetyIdentifier'
import { setOpenModalTryPremium, setUser, setUserIsPremium, setUserIsSuper } from '@/store/slices/user/actions'
import { SignInManager } from '@/scenes/Editor/components/Navbar/components/SignInManager'
import { customAmplitude } from '@/utils/customAmplitude'
import { AuthToken, UserInfo, User } from '@/common/interfaces'
import ImageJs from 'image-js'
import { AI_STYLE_QUALITY } from '@/scenes/engine/common/constants'
import { MagicDesignTextInfo } from '@/scenes/engine/objects/MagicDesignTextInfo'
import { Rectangle } from "@/shared/sync/mediaRepository/rectangle"
import { MediaImageType } from '@/shared/sync/mediaRepository/media_image_type'
import { UrlHelpers } from '@/shared/services/UrlHelpers'
import { SupportedFeatureOnWebValues } from '@/shared/sync/SupportedFeature'
import type { LabeledImageItem, ClassificationGroup } from '@/store/slices/types'
import { buildDefaultInstruction } from '@/scenes/engine/utils/aiFashionConstants'

import {
  buildGeminiGenerationConfig,
  buildGeminiParts,
  extractGeminiContentFromApiResponse,
  extractGeminiText,
  GeminiGenerateParams
} from '@/services/gemini/geminiHelpers'
import { appendOpenAiEditMaskToFormData } from '@/services/openai/openAiImageEditHelpers'
import { setShowModalCustomText } from '@/store/slices/modal/customTextModal'
import { setOpenModalOutOfCredits } from '@/store/slices/user/actions'

type Template = any
type SuggestionItem = { title: string; isPlaceholder: boolean; prompt: string; icon: string }
type AIRequestBehavior = {
  suppressGlobalError?: boolean
  retry?: boolean
}
export interface OpenAiResponseFormat {
  type: 'json_schema'
  json_schema: {
    name: string
    strict: boolean
    schema: Record<string, unknown>
  }
}
let accessToken = localStorage.getItem('access_token');
const ACCESS_TOKEN_STORAGE_KEY = 'access_token'

/** Response from GET /purchase/txid/<id>/ (management URLs for Paddle Billing / refreshed Classic). */
export type PurchaseByTxidResponse = {
  paddle_update_url?: string | null
  paddle_cancel_url?: string | null
  platform?: string | null
  original_transaction_id?: string | null
}

// ─── Home feed (iOS HomeFeedManager parity) ─────────────────────────────────
// AI classifications 113/132 are the original "AI Video" categories on iOS
// (`MagicTemplateCategoryID.isAIVideo`). Newer ones (142/143, and anything the
// backend adds later) are NOT in this list, so it is only a fast path — the
// authoritative checks live in `resolveAiSectionKind`.
export const AI_VIDEO_CLASSIFICATION_IDS: readonly number[] = [113, 132]
/** Backend `category.type` marking a classification as AI-video. */
const AI_VIDEO_CATEGORY_TYPE = 'ai_videos'
// iOS caps AI section previews at 16 items (`loadTemplateSection(maxItems:)`).
const HOME_FEED_AI_PREVIEW_MAX = 16

/** One AI (magic template / AI-video) item inside a resolved home-feed section. */
export interface HomeFeedAiItem {
  id: number
  title: string
  prompt: string
  assets: Array<{ file: string }>
  /** Motion prompt for AI-video items (iOS `videoPrompt`); null for plain AI items. */
  videoPrompt: string | null
  isSuper: boolean
  quality?: number
  model?: string
}

/** One regular template (`template_upload`) item inside a resolved home-feed section. */
export interface HomeFeedTemplateItem {
  id: number
  name?: string
  image: string
  width?: number
  height?: number
}

/**
 * An AI (magic template) or AI-video home-feed section resolved to its items.
 * Both share the same shape; `kind` distinguishes plain AI from AI-video (113/132).
 */
export interface HomeFeedAiSection {
  kind: 'ai' | 'ai-video'
  id: number
  title: string
  order: number
  aspectRatio: number
  items: HomeFeedAiItem[]
}

/** A regular template (`template_upload`) home-feed section resolved to its items. */
export interface HomeFeedTemplateSection {
  kind: 'template'
  id: number
  title: string
  order: number
  items: HomeFeedTemplateItem[]
}

/**
 * A home-feed section resolved to its item list. `kind` is derived from which
 * embedded object the backend section carries (mirrors iOS `HomeFeedManager.parse`).
 */
export type HomeFeedResolvedSection = HomeFeedAiSection | HomeFeedTemplateSection

/** Raw `/home_feed_section/` object; embedded classification/category may be camel or snake case. */
interface HomeFeedSectionRaw {
  id: number
  title?: string
  order?: number
  is_hidden?: boolean
  isHidden?: boolean
  ai_classification?: any
  aiClassification?: any
  template_category?: any
  templateCategory?: any
}

/**
 * First non-blank title. The `/home_feed_section/` object usually has an empty
 * `title` ("") and the real title lives on the embedded classification/category,
 * so `??` (which only falls through on null/undefined) isn't enough here.
 */
function firstNonEmptyTitle(...candidates: Array<string | undefined | null>): string {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return ''
}

function mapHomeFeedAiItem(raw: any): HomeFeedAiItem {
  const videoPrompt = (raw?.video_prompt ?? raw?.videoPrompt ?? '') || ''
  return {
    id: raw.id,
    title: raw.title ?? '',
    prompt: raw.prompt ?? '',
    assets: Array.isArray(raw.assets) ? raw.assets : [],
    videoPrompt: videoPrompt.trim() ? videoPrompt : null,
    isSuper: Boolean(raw?.is_super ?? raw?.isSuper),
    quality: raw.quality,
    model: raw.model_version,
  }
}

/**
 * Whether an AI classification is an AI-video section. Checks, in order: the
 * legacy hardcoded ids, the embedded category type, and finally the items
 * themselves — AI-video items carry a motion `videoPrompt` and a single video
 * asset, so a newly added AI-video category is classified correctly even before
 * anyone updates `AI_VIDEO_CLASSIFICATION_IDS`. Getting this wrong renders a
 * video section through the before/after image row, which shows nothing.
 */
function resolveAiSectionKind(
  classificationId: number,
  aiClassification: any,
  items: HomeFeedAiItem[],
): 'ai' | 'ai-video' {
  if (AI_VIDEO_CLASSIFICATION_IDS.includes(classificationId)) return 'ai-video'
  const categoryType =
    aiClassification?.category?.type ??
    aiClassification?.category_type ??
    aiClassification?.categoryType
  if (typeof categoryType === 'string' && categoryType === AI_VIDEO_CATEGORY_TYPE) return 'ai-video'
  if (items.some(item => item.videoPrompt)) return 'ai-video'
  return 'ai'
}

function mapHomeFeedTemplateItem(raw: any): HomeFeedTemplateItem | null {
  if (!raw?.image) return null
  return { id: raw.id, name: raw.name, image: raw.image, width: raw.width, height: raw.height }
}

/** A `template_upload` category is a "video" category (excluded from the feed). */
function isVideoTemplateCategory(templateCategory: any): boolean {
  if (!templateCategory) return false
  if (templateCategory.contains_video || templateCategory.is_video) return true
  return /video/i.test(String(templateCategory.name ?? ''))
}
/**
 * TODO(yadid): replace with the studio-only OpenAI Responses prompt id/version.
 * Until then this reuses the mixed photoshoot shot-plan prompt (`variants=9`).
 */
export const STUDIO_ONLY_SHOT_PLAN_PROMPT_ID = 'pmpt_6a4e0b1822648195aabcf5f602110e9a03855032542ce29e'
export const STUDIO_ONLY_SHOT_PLAN_PROMPT_VERSION = '33'

class ApiService {
  api: AxiosInstance
  global: AxiosInstance
  isRefreshing: boolean = false;
  refreshSubscribers: Array<(token: string) => void> = [];
  // Monotonic counter bumped by every clearAccessToken/setAccessToken. An
  // in-flight refresh snapshots this before kicking off and compares on
  // resolution; if it changed (logout, fresh login, etc.) the stale refresh
  // result is discarded — no token write, no subscription sync.
  private accessTokenRevision = 0

  private parseDataUrl(dataUrl: string): { mimeType: string; data: string } {
    const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/)
    if (match) {
      return { mimeType: match[1], data: match[2] }
    }
    return {
      mimeType: 'image/png',
      data: dataUrl.replace(/^data:image\/[^;]+;base64,/, ''),
    }
  }

  private parseSuggestionItems(response: any, errorPrefix: string): SuggestionItem[] {
    try {
      const sanitizedResponse =
        typeof response === 'string'
          ? response.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '')
          : response
      const parsed = typeof sanitizedResponse === 'string' ? JSON.parse(sanitizedResponse) : sanitizedResponse
      const suggestions = Array.isArray(parsed?.editing_suggestions) ? parsed.editing_suggestions : parsed

      if (!Array.isArray(suggestions)) {
        return []
      }

      return suggestions.map((item: any) => {
        if (typeof item === 'object' && item !== null) {
          return {
            title: item.title || '',
            isPlaceholder: false,
            prompt: item.prompt || '',
            icon: item.icon || '',
          }
        }

        return {
          title: String(item),
          isPlaceholder: false,
          prompt: String(item),
          icon: '',
        }
      })
    } catch (error) {
      console.error(errorPrefix, error)
      return []
    }
  }

  private dataUrlToBlobPreservingMime(dataUrl: string): Blob {
    const { mimeType, data } = this.parseDataUrl(dataUrl)
    const binary = atob(data)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return new Blob([bytes], { type: mimeType })
  }

  private fileExtensionFromMime(mimeType: string): string {
    if (mimeType === 'image/jpeg') return 'jpg'
    if (mimeType === 'image/png') return 'png'
    const extension = mimeType.split('/')[1]
    return extension || 'bin'
  }

  private async pollUntilTerminal<T>(
    fetchStatus: () => Promise<T>,
    getStatus: (item: T) => string | undefined,
    intervalMs: number,
    maxAttempts: number = 20
  ): Promise<T> {
    let latest: T
    for (let i = 0; i < maxAttempts; i++) {
      latest = await fetchStatus()
      const status = getStatus(latest)?.toLowerCase()
      if (status === 'completed' || status === 'failed') return latest
      if (i < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, intervalMs))
      }
    }
    return latest
  }

  constructor() {

    this.global = axios.create()
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      withCredentials: true,  // Send HTTP-only cookies
    })
    this.setupRequestInterceptor()
    this.setupResponseInterceptor()
  }

  private getResponseHeaderValue(headers: any, headerName: string): string | null {
    if (!headers) {
      return null
    }

    const normalizedHeaderName = headerName.toLowerCase()
    const headerValue = headers[normalizedHeaderName] ?? headers[headerName]

    if (Array.isArray(headerValue)) {
      return headerValue.length > 0 ? String(headerValue[0]) : null
    }

    return headerValue != null ? String(headerValue) : null
  }

  // Email auth POSTs to `/token/` are surfaced as inline errors inside the
  // sign-in screens (wrong password / wrong magic code / send-code failure).
  // Skip the global "Uh-oh!" modal for those so the sign-in modal stays open
  // and the inline message is the only thing the user sees.
  private isInlineHandledAuthError(error: any): boolean {
    const config = error?.config
    if (!config) {
      return false
    }
    const method = typeof config.method === 'string' ? config.method.toLowerCase() : ''
    if (method !== 'post') {
      return false
    }
    const url = typeof config.url === 'string' ? config.url : ''
    let body: any
    try {
      body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data
    } catch {
      return false
    }
    const isBazaartTokenRequest = url.endsWith('/token/') && body?.identity_provider === 'bazaart'
    const isPasswordSignUpRequest =
      url.endsWith('/user/') &&
      typeof body?.email === 'string' &&
      typeof body?.password === 'string' &&
      !body?.identity_provider

    return isBazaartTokenRequest || isPasswordSignUpRequest
  }

  private isReconnectNetworkError(error: any): boolean {
    if (error?.response) {
      return false
    }

    const code = typeof error?.code === 'string' ? error.code.toUpperCase() : ''
    const message = typeof error?.message === 'string' ? error.message.toUpperCase() : ''
    const networkMarkers = ['ERR_NETWORK', 'ERR_NETWORK_CHANGED', 'ERR_INTERNET_DISCONNECTED']

    if (networkMarkers.some(marker => code.includes(marker) || message.includes(marker))) {
      return true
    }

    if (axios.isAxiosError(error) && !error.response) {
      return message.includes('NETWORK ERROR') || message.includes('FAILED TO FETCH')
    }

    return false
  }

  // Silent on post-idle background 401s; user-initiated paths are unaffected.
  private isBackgroundAuthError(error: any): boolean {
    if (error?.response?.status !== 401) return false

    const url: string =
      (typeof error?.config?.url === 'string' && error.config.url) ||
      (typeof error?.response?.config?.url === 'string' && error.response.config.url) ||
      ''
    if (!url) return false

    const path = url.split('?')[0]
    return (
      path.endsWith('/locate/me/') ||
      path.endsWith('/purchase/') ||
      path.includes('/photo_item/') ||
      /\/user\/\d+\/?$/.test(path)
    )
  }

  private subscribeTokenRefresh(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb)
  }

  private onRefreshed(token: string) {
    this.refreshSubscribers.forEach((cb) => cb(token))
    this.refreshSubscribers = []
  }

  public clearAccessToken(): void {
    this.accessTokenRevision += 1
    accessToken = ''
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
  }

  private setAccessToken(token: string): void {
    this.accessTokenRevision += 1
    accessToken = token
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token)
  }

  /**
   * Refresh the access token and update all relevant storages. Only one refresh
   * request can be performed at any given time. Calls waiting for a token will
   * be resolved once the refresh completes.
   */
  public refreshAccessToken(): Promise<string> {
    return new Promise((resolve) => {
      this.subscribeTokenRefresh(resolve)
      if (!this.isRefreshing) {
        this.isRefreshing = true
        // Snapshot the token revision so we can detect a logout (or any other
        // token write) that happened while this refresh was in flight. Two
        // checkpoints — before and after the acknowledgeToken await — cover
        // the full async window.
        const refreshStartedAtRevision = this.accessTokenRevision
        this.refreshToken()
          .then(async (newToken) => {
            if (newToken) {
              if (refreshStartedAtRevision !== this.accessTokenRevision) {
                this.onRefreshed('')
                return
              }
              try {
                await this.acknowledgeToken(newToken)
              } catch (e) {
                console.error("Acknowledge token failed.")
              }
              if (refreshStartedAtRevision !== this.accessTokenRevision) {
                this.onRefreshed('')
                return
              }
              this.setAccessToken(newToken)
              SignInManager.getInstance().syncSubscriptionFlagsFromAccessToken()
            }
            this.onRefreshed(accessToken)
          })
          .catch(() => {
            this.onRefreshed('')
          })
          .finally(() => {
            this.isRefreshing = false
          })
      }
    })
  }

  // Interceptor
  setupRequestInterceptor() {
    this.api.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // An explicit bearer token belongs to this request, not the current
        // session (for example, a profile lookup during sign-in).
        if (config.headers['Authorization']) return config
        if (!accessToken) {
          accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || '';
        }
  
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        } else {
          let userInfo = UserInfoStorage.get()
          if (userInfo && userInfo.accessToken) {
            let new_access_token = await this.exchangeToken(userInfo.accessToken)
            if (new_access_token) {
              this.setAccessToken(new_access_token)
              delete userInfo.accessToken
              const signInManager = SignInManager.getInstance()
              signInManager.setUserProperties(userInfo)
              // setUserProperties populates the singleton from the legacy blob;
              // sync now re-derives premium/super from the freshly-exchanged
              // JWT so Redux and UserInfoStorage reflect token-authoritative
              // tier (legacy userInfo may carry a stale userIsPremium flag).
              signInManager.syncSubscriptionFlagsFromAccessToken()
              config.headers['Authorization'] = `Bearer ${accessToken}`;
            }
            else {
              delete config.headers.Authorization;
            }
          }
          else {
            delete config.headers.Authorization;
          }
        }

        if(this.tokenExpiryCheck()) {
          return new Promise<InternalAxiosRequestConfig>((resolve) => {
            this.refreshAccessToken().then((token) => {
              if(token) {
                config.headers['Authorization'] = `Bearer ${token}`
              }
              resolve(config)
            })
          })
        }
        return config
      },
      error => Promise.reject(error)
    )
  }

  setupResponseInterceptor() {
    this.api.interceptors.response.use(
      response => response,
      error => {
        if (error?.code === "ERR_CANCELED") {
          return Promise.reject(error)
        }
        if (error?.config?.bazaartSuppressGlobalError === true) {
          return Promise.reject(error)
        }
        if (this.isInlineHandledAuthError(error)) {
          return Promise.reject(error)
        }
        if (this.isReconnectNetworkError(error)) {
          return Promise.reject(error)
        }
        if (this.isBackgroundAuthError(error)) {
          return Promise.reject(error)
        }
        const status = error?.response?.status
        const errorCode = this.getResponseHeaderValue(error?.response?.headers, 'x-error-code')

        if (status === 403 && errorCode === '1000') {
          store.dispatch(setOpenModalOutOfCredits({ isOpen: true }))
        } else {
          setShowModalCustomText('Uh-oh! 😬', 'Something went wrong. Please try again.', 'Ok', 'Cancel')
        }

        return Promise.reject(error)
      },
    )
  }

  // Authentication
  tokenExpiryCheck(): boolean {
    const access_token = accessToken
    if (access_token) {
      const decoded = SignInManager.getInstance().decodeJwtResponse(access_token)
      const currentTime = Math.floor(Date.now() / 1000);
      const expireSoon = (decoded.exp - currentTime) < 60
      return expireSoon; // Returns true if token will expire in less than 60 seconds
    }
    return false;
  }; 

  refreshFirebaseToken(userId: number): Promise<string> {
    return new Promise((resolve, reject) => {
      // omg - it's essential that we append '/' to the end of the login path
      this.api.get(`/user/${userId}/`, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json; charset=utf-8",
          "Referrer-Policy": "origin",
        }
      })
      .then(({ data }) => {
        resolve(data?.firebase_token)
      })
      .catch(err => reject(err))
    })
  }

  exchangeToken(oldToken:string): Promise<string>  {
    return new Promise((resolve, reject) => {
      let params = {
        "identity_provider": "bazaart",
        "authorization_code": oldToken
      }
      // omg - it's essential that we append '/' to the end of the login path
      axios.post(
        `${process.env.REACT_APP_API_URL}/token/`, params, { withCredentials: true })
        .then(({ data }) => {
          if (data && data.id) {
            delete data.refresh_token
            resolve(data.access_token);
          } else {
            reject(new Error("Login response does not contain user ID."));
          }
        })
        .catch(err => reject(err))
    })
  }
  
  /**
   * Common handler for auth token responses. Stores the access token,
   * fetches user data, and returns the combined user info.
   */
  private async handleAuthResponse(data: AuthToken, errorMessage: string, signal?: AbortSignal): Promise<UserInfo> {
    if (data && data.id) {
      delete data.refresh_token
      const throwIfCancelled = () => {
        if (signal?.aborted) throw Object.assign(new Error('Login cancelled'), { code: 'ERR_CANCELED' })
      }
      throwIfCancelled()
      const previousToken = accessToken || localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || ''
      this.setAccessToken(data.access_token)
      const loginRevision = this.accessTokenRevision
      const rollbackCancelledLogin = () => {
        if (this.accessTokenRevision !== loginRevision) return
        if (previousToken) this.setAccessToken(previousToken)
        else this.clearAccessToken()
      }
      signal?.addEventListener('abort', rollbackCancelledLogin, { once: true })
      try {
        const userData = await this.getUserById(data, signal, signal ? data.access_token : undefined)
        throwIfCancelled()
        return { ...data, ...userData }
      } catch (err) {
        if (!signal?.aborted) console.error("Failed to fetch user by ID:", err)
        throw err
      } finally {
        signal?.removeEventListener('abort', rollbackCancelledLogin)
      }
    }
    throw new Error(errorMessage)
  }

  login(sessionToken: string, type: string, userId: string, signal?: AbortSignal): Promise<UserInfo> {
    return new Promise((resolve, reject) => {
      let params = {
        identity_provider: type,
        authorization_code: sessionToken,
        user_id: userId
      };
      // omg - it's essential that we append '/' to the end of the login path
      this.api
        .post<AuthToken>('/token/', params, {
          signal,
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json; charset=utf-8",
            "Referrer-Policy": "origin"
          }
        })
        .then(({ data }) => {
          this.handleAuthResponse(data, "Login response does not contain user ID.", signal)
            .then(resolve)
            .catch(reject)
        })
        .catch(err => reject(err))
    })
  }

  /**
   * Cross-subdomain SSO redeem (design.bazaart.com). Exchanges a one-time
   * sso_code minted on photoshoot.bazaart.com for a fresh access token. The
   * refresh token is delivered exclusively as an HttpOnly cookie, so
   * `withCredentials` is mandatory. No `platform` header is sent (its absence
   * marks this as a web client). The response mirrors `/token/` (`id` +
   * `access_token`), so it reuses the shared `handleAuthResponse` plumbing.
   */
  redeemSsoCode(code: string): Promise<UserInfo> {
    return new Promise((resolve, reject) => {
      // Raw axios (not `this.api`) so the request interceptor never attaches a
      // stale Bearer/refresh and no `platform` header leaks in. omg - it's
      // essential that we append '/' to the end of the path.
      axios.post<AuthToken>(
        `${process.env.REACT_APP_API_URL}/sso_redeem/`,
        { code },
        { withCredentials: true }
      )
        .then(({ data }) => {
          this.handleAuthResponse(data, "SSO redeem response does not contain user ID.")
            .then(resolve)
            .catch(reject)
        })
        .catch(err => reject(err))
    })
  }

  loginWithEmailPassword(email: string, password: string): Promise<UserInfo> {
    return new Promise((resolve, reject) => {
      let params = {
        identity_provider: 'bazaart',
        email: email,
        password: password
      };
      this.api
        .post<AuthToken>('/token/', params, {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json; charset=utf-8",
            "Referrer-Policy": "origin"
          }
        })
        .then(({ data }) => {
          this.handleAuthResponse(data, "Login response does not contain user ID.")
            .then(resolve)
            .catch(reject)
        })
        .catch(err => reject(err))
    })
  }

  registerWithEmailPassword(email: string, password: string): Promise<AuthToken> {
    return new Promise((resolve, reject) => {
      let params = {
        email: email,
        password: password,
        first_name: "",
        last_name: "",
      };
      this.api
        .post<AuthToken>('/user/', params, {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json; charset=utf-8",
            "Referrer-Policy": "origin"
          }
        })
        .then(({ data }) => resolve(data))
        .catch(err => reject(err))
    })
  }

  sendMagicCode(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      let params = {
        identity_provider: 'bazaart',
        email: email
      };
      this.api
        .post('/token/', params, {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json; charset=utf-8",
            "Referrer-Policy": "origin"
          }
        })
        .then(() => resolve())
        .catch(err => reject(err))
    })
  }

  verifyOTPCode(email: string, otpCode: string): Promise<UserInfo> {
    return new Promise((resolve, reject) => {
      let params = {
        identity_provider: 'bazaart',
        email: email,
        otp_code: otpCode
      };
      this.api
        .post<AuthToken>('/token/', params, {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json; charset=utf-8",
            "Referrer-Policy": "origin"
          }
        })
        .then(({ data }) => {
          this.handleAuthResponse(data, "OTP verification failed.")
            .then(resolve)
            .catch(reject)
        })
        .catch(err => reject(err))
    })
  }

  listTransactions(): Promise<any> {
    return new Promise((resolve, reject) => {
      // omg - it's essential that we append '/' to the end of the purchase path
      this.api
        .get('/purchase/', {
          headers: {
            Accept: "application/json",
          }
        })
        .then(({ data }) => {
          resolve(data)
        })
        .catch(err => {
          if (
            err.code === "ERR_CANCELED" ||
            err.code === "ECONNABORTED" ||
            this.isReconnectNetworkError(err) ||
            this.isBackgroundAuthError(err)
          ) {
            resolve(null)
          } else {
            this.clearAccessToken()
            UserInfoStorage.remove()
            syncOneSignalUser(null)
            store.dispatch(setUser(null))
            store.dispatch(setUserIsPremium(false))
            store.dispatch(setUserIsSuper(false))
            store.dispatch(
              setOpenModalTryPremium({
                isOpen: false,
                source: null,
              })
            )
            reject(err)
          }
        })
    })
  }

  /** Reads billing period and price identity from the selected purchase's Store. */
  async getSubscriptionStore(storeId: number): Promise<SubscriptionStore> {
    if (!Number.isSafeInteger(storeId) || storeId <= 0) throw new Error('Invalid subscription store id')
    const { data } = await this.api.get(`/store/${storeId}/`)
    return parseSubscriptionStore(data, storeId)
  }

  getPurchaseByTransactionId(originalTransactionId: string): Promise<PurchaseByTxidResponse> {
    const trimmed = originalTransactionId?.trim()
    if (!trimmed) {
      return Promise.reject(new Error('Missing subscription id'))
    }
    return new Promise((resolve, reject) => {
      const id = encodeURIComponent(trimmed)
      this.api
        .get(`/purchase/txid/${id}/`, {
          headers: {
            Accept: 'application/json',
          },
        })
        .then(({ data }) => {
          resolve(data as PurchaseByTxidResponse)
        })
        .catch((err) => reject(err))
    })
  }

  /**
   * Switches an existing Paddle Billing subscription to a new price in place via
   * PATCH /purchase/txid/<subscriptionId>/.
   *
   * Mirrors the iOS `updatePaddleSubscriptionPrice` contract: the JSON body uses
   * `proration_billing_mode` and `store` (the target Paddle price id, e.g.
   * `pri_...`). This is the canonical way to change an active subscriber's plan
   * without opening a second checkout (which would create a parallel
   * subscription). Resolves with the updated purchase resource.
   */
  updatePaddleSubscriptionPrice(
    subscriptionId: string,
    targetPriceId: string,
    prorationBillingMode: string = 'prorated_immediately',
  ): Promise<PurchaseByTxidResponse> {
    const trimmedId = subscriptionId?.trim()
    const trimmedPrice = targetPriceId?.trim()
    if (!trimmedId || !trimmedPrice) {
      return Promise.reject(new Error('Missing subscription id or target price id'))
    }
    return new Promise((resolve, reject) => {
      const id = encodeURIComponent(trimmedId)
      this.api
        .patch(
          `/purchase/txid/${id}/`,
          { proration_billing_mode: prorationBillingMode, store: trimmedPrice },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          },
        )
        .then(({ data }) => {
          resolve(data as PurchaseByTxidResponse)
        })
        .catch((err) => reject(err))
    })
  }

  refreshToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      const body = {
        access_token: accessToken, //expired or going to expire token
      }
      axios.post(
        `${process.env.REACT_APP_API_URL}/refresh/`,
        body,
        { withCredentials: true },
      ).then((res) => {
        resolve(res?.data?.access_token);
      // this.api.post('/refresh/', {access_token: accessToken}, { withCredentials: true }).then((res) => {
        // TODO: Remove the storage of the refreshToken in Cookies when server-side set-cookie header
        // let cookies = new Cookies()
        // let userInfo = cookies.get('userInfo')
        // if(userInfo) {
        //   cookies.set('refresh_token', res.data.refresh_token)
        // }
        // accessToken = res.data.access_token;
        // let cookies = new Cookies()
        // let userInfo = cookies.get('userInfo')
        // if(userInfo) {
        //   cookies.set('userInfo', {
        //     ...userInfo,
        //     access_token: accessToken,
        //   })
        // }
        // resolve(accessToken);

        // return this.acknowledgeToken(res.data.access_token)
        //   .then((acknowledgedToken) => resolve(acknowledgedToken))
        //   .catch(() => {
        //     console.warn("Acknowledge failed, continuing with the temporary access token.");
        //     resolve(res.data.access_token); // Proceed with temporary token even if acknowledgment fails
        //   });
      })
      .catch(() => {
        console.error("Refresh token failed.")
        resolve('') // Proceed with old access token if refresh fails
      })
    })
  }

  acknowledgeToken(newAccessToken): Promise<any> {
    return new Promise((resolve, reject) => {
      let body = {
        access_token: newAccessToken
      }
      axios.post(
        `${process.env.REACT_APP_API_URL}/acknowledge/`,
        body,
        { withCredentials: true },
      ).then((res) => {
        resolve(res.data.access_token)
      })
      .catch(() => {
        console.error("Acknowledge token failed.")
        reject()
      })
    })
  }

  // User
  getUserById(data: { id: number }, signal?: AbortSignal, token?: string): Promise<User> {
    return new Promise((resolve, reject) => {
      this.api
        .get(`/user/${data.id}/`, {
          signal,
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        })
        .then(({ data }) =>{
          // const contentLength = data.headers.get('content-length');
          // if (contentLength) {
          //   console.log(`Content Length from header: ${contentLength} bytes`);
          // }
          resolve(data)
        })
        .catch((err) => {
          if (!signal?.aborted) console.error("Error fetching user by ID:", err);
          reject(err); // Reject if fetching user by ID fails
        });
    });
  }

  /**
   * Server-side locate for the current user. Requires a session the API client can
   * authorize (access_token or userInfo.accessToken for exchange). Resolves to
   * GET {REACT_APP_API_URL}/locate/me/ (version comes from env, e.g. /api/v8).
   */
  async locate(): Promise<any> {
    const token = accessToken || localStorage.getItem('access_token') || ''
    const userInfo = UserInfoStorage.get()
    const canAuthorize =
      !!token.trim() || !!(userInfo && userInfo.accessToken)

    if (!canAuthorize) {
      throw new Error('Authentication required')
    }

    const { data } = await this.api.get('/locate/me/', {
      headers: {
        Accept: 'application/json',
      },
    })
    return data
  }

  // UPLOADS
  getSignedURLForUpload(props: { name: string }): Promise<{ url: string }> {
    return new Promise((resolve, reject) => {
      this.api
        .post('/uploads', props)
        .then(({ data }) => {
          resolve(data)
        })
        .catch(err => reject(err))
    })
  }

  updateUploadFile(props: { name: any }): Promise<any> {
    return new Promise((resolve, reject) => {
      // this.base
      //   .put('/uploads', props)
      //   .then(({ data }) => {
      //     resolve(data)
      //   })
      //   .catch(err => reject(err))
      resolve({ url: props.name })
    })
  }

  getUploads(): Promise<IUpload[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('/photo_item/?store=326')
        var data_m = data.objects.map(x => {
          return {
            id: x.id.toString(),
            url: x.image,
            name: x.image,
            folder: '/',
            contentType: '',
            type: '',
          }
        })
        var d = data_m as unknown as IUpload[]
        resolve(d)
      } catch (err) {
        reject(err)
      }
    })
  }

  deleteUpload(id: string) {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.api.delete(`/uploads/${id}`)
        resolve(response)
      } catch (err) {
        reject(err)
      }
    })
  }


  getTemplatesSuperCategories(): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/templateuploadcategorycollection/?is_website_only=0&is_video=0`)
        const superCategories = data.objects.filter(x => x.name != 'Video')
        resolve(superCategories)
      } catch (err) {
        reject(err)
      }
    })
  }

  getTemplateDetails(templateId: string): Promise<TemplateDetails> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/template_upload/${templateId}/?version__lte=1000&is_hidden=1`)
        resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }

  // getCategoreisByTemplate(templateId: string): Promise<any[]> {
  //   return new Promise(async (resolve, reject) => {
  //     try {
  //       const { data } = await this.api.get(`/template_upload/${templateId}/?version__lte=1000`)
  //       resolve(
  //         data.categories
  //           .map(x => {
  //             var parts = x.split("/")
  //             var lastSegment = parts.pop() || parts.pop()
  //             return Number(lastSegment)
  //           }))
  //     } catch (err) {
  //       reject(err)
  //     }
  //   })
  // }

  getTemplatesByCategory(id: number, page: number = 0): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const featuresFilter = SupportedFeatureOnWebValues.join(",");
        const { data } = await this.api.get(
          `/template_upload/?categories=${id}&limit=100&offset=${page * 100}&features_filter=${featuresFilter}&is_hidden=1&version__lte=1000`
        )
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  getCategoriesBySuperCategory(id: number): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(
          `/admin_template_category/?collections=${id}&locales=he&isHidden=false&contains_video=false&limit=100&version__lte=13`
        )
        resolve(data.objects.filter(x => x.is_web_visible))
      } catch (err) {
        reject(err)
      }
    })
  }

  searchTemplate(querty, signal?: AbortSignal): Promise<IStorePack[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const featuresFilter = SupportedFeatureOnWebValues.join(",");

        const { data } = await this.api.get(
          `/template_upload/?offset=0&limit=0&version__lte=1000&search=${querty}&features_filter=${featuresFilter}&is_hidden=1`,
          {signal}
        )
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }
  getCategoryDetails(id: number): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(
          `/admin_template_category/${id}/`
        )
        resolve(data.is_web_visible ? data : null)
      } catch (err) {
        reject(err)
      }
    })
  }
  //CREATIONS

  createCreation(props: Partial<Template>): Promise<Template> {
    return new Promise((resolve, reject) => {
      this.api
        .post('/creations', props)
        .then(({ data }) => {
          resolve(data)
        })
        .catch(err => reject(err))
    })
  }

  getCreations(): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('/creations')
        resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }

  async listAllTemplateAsset(templateId: string, signal?: AbortSignal): Promise<any> {
    let assets = []
    let off = 0
    let assets_step
    do {
      assets_step = await this.api.get(`/templateasset/?limit=100&offset=${off * 100}&template=` + templateId, { signal })
      assets.push(...assets_step.data.objects)
      off++
    } while (assets_step.data.meta.total_count > assets.length)
    return assets
  }

  loadTemplateAsset(templateId: string, type: MediaImageType, layerName?: string, signal?: AbortSignal): Promise<any> {
    try {
      let assetPromise;
      if (layerName) {
        assetPromise = this.api.get(`/templateasset/?type=${type}&layer=${layerName}&template=${templateId}&order_by=-timestamp&limit=1`, { signal })
      } else {
        assetPromise = this.api.get(`/templateasset/?type=${type}&template=${templateId}&order_by=-timestamp`, { signal })
      }
      return assetPromise;
    } catch (err) {
      if (err.code === "ERR_CANCELED") {
        return null;
      } else {
        return err;
      }
    }
  }

  getCreationById(id: string, signal?: AbortSignal): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        let configJsonPromise = this.loadTemplateAsset(id, MediaImageType.original, "config.json", signal);
        let layersJsonPromise = this.loadTemplateAsset(id, MediaImageType.original, "layer.json", signal);
        let latestAssetPromise = this.loadTemplateAsset(id, MediaImageType.latest, null, signal);

        return Promise.all([configJsonPromise, layersJsonPromise, latestAssetPromise]).then((assets) => {
          const jsonConfigUrl = assets[0]?.data?.objects[0]?.asset;
          const jsonLayersUrl = assets[1]?.data?.objects[0]?.asset;
          const latestAssets = assets[2]?.data?.objects;

          const downloadJsonConfigPromise = this.downloadAsset(jsonConfigUrl, { signal })
          const downloadJsonLayersPromise = this.downloadAsset(jsonLayersUrl, { signal })

          return Promise.all([downloadJsonConfigPromise, downloadJsonLayersPromise]).then((configAssets) => {
            let jsonConfig = configAssets[0];
            let jsonLayers = configAssets[1];
            let BtDraftObject = jsonConfig?.data?.BtDraftObject
            //TODO: remove this line.it temp workaround becauase AI tempaltes not return templateId - Dror should fix it on upload template flow
            BtDraftObject.templateId = id;
            resolve({ objects: jsonLayers?.data, latestAssets: latestAssets, config: BtDraftObject })
          });
        })
      } catch (err) {
        if (err.code === "ERR_CANCELED") {
          resolve(false)
        } else {
          reject(err)
        }
      }
    })
  }
  updateCreation(id: string, props: Partial<Template>): Promise<Template> {
    return new Promise((resolve, reject) => {
      this.api
        .put(`/creations/${id}`, props)
        .then(({ data }) => {
          resolve(data)
        })
        .catch(err => reject(err))
    })
  }

  // ELEMENTS
  getElements(): Promise<IElement[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('/elements')
        resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }
  updateTemplateLayout(id: number, props: Partial<Template>): Promise<Template> {
    // let params =
    return new Promise((resolve, reject) => {
      this.api
        .post(`/templateasset/`, props)
        .then(({ data }) => {
          resolve(data)
        })
        .catch(err => reject(err))
    })
  }

  // FONTS
  getFontPacks(): Promise<IFontFamily[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('/store/?category__in=3&order_by=priority')
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  getFonts(): Promise<IFontFamily[]> {
    return new Promise(async (resolve, reject) => {
      try {
        // const { data } = await this.base.get('/photo_item/?store__category__in=3&limit=1000&offset=0')
        const { data } = await this.api.get(
          '/photo_item/?store__category__in=3&limit=1000&offset=0'
        )
        // console.log('data', data)

        var data_m = data.objects.map(x => {
          return {
            id: x.id.toString(),
            family: x.font_system_name,
            files: { regular: x.file },
            variants: ['regular'],
            subsets: ['latin'],
            version: 'v1',
            lastModified: '2022-03-01',
            kind: 'webfonts#webfont',
            category: x.source_platform,
            desc: x.description,
            name: x.font_nickname,
          }
        })
        var d = data_m as unknown as IFontFamily[]
        resolve(d)
      } catch (err) {
        reject(err)
      }
    })
  }

  getBackgroundPacks(): Promise<IStorePack[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('/store/?category__in=11,12&order_by=priority')
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  searchStickers(querty, signal?: AbortSignal): Promise<IStorePack[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/photo_item/?search=${querty}`, { signal })
        const dataWithoutAnimated = data.objects.filter(x => !x.image.endsWith('gif'))
        resolve(dataWithoutAnimated)
      } catch (err) {
        reject(err)
      }
    })
  }

  getGraphicPacks(): Promise<IStorePack[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('/store/?category__in=14,1&order_by=priority')
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  getFilterPacks(): Promise<IStorePack[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('/store/?category__in=15&order_by=priority')
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  getPack(packId: string): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('/photo_item/?store=' + packId)
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }


  bgRemove(image: any, width: string, height: string, abortSignal?: AbortSignal): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        customAmplitude('Magic Cut Out')

        let formData = new FormData()
        formData.append('image', image)
        formData.append('width', width)
        formData.append('height', height)

        const config = {
          headers: {
            'content-type': 'multipart/form-data',
          },
          signal: abortSignal,
        }

        const { data } = await this.api.post('/bgremove', formData, config)

        resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }

  getFilters(): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('store_category/')
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  getImageElement(): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get('/photo_item/?store__category=15&limit=1000&offset=0')
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  inpaintImage(formData): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        customAmplitude('Magic Heal')
        const config = {
          headers: {
            'content-type': 'multipart/form-data',
          },
          maxRedirects: 10
        }

        const data = await this.api.post('/bheal/', formData, config)
        resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }

  prepareUrl(inputUrl: any): string {
    return UrlHelpers.prepareUrl(inputUrl);
  }

  downloadAsset(inputUrl: URL, inputParams: any = null): Promise<any> {
    let url = this.prepareUrl(inputUrl);
    return new Promise(async (resolve, reject) => {
      try {
        let params = {
          url: url,
          method: 'GET',
          crossOrigin: 'Anonymous'
        }
        params = Object.assign(params, inputParams || {});
        const result = await this.global(params)
        resolve(result)
      } catch (err) {
        reject(err)
      }
    })
  }
  async enhance(base64image: any, upscale_factor: string = "1"): Promise<any> {
    // let maxSupportedEdge = 1024
    // let minSupportedEdge = 512


    // const base64Prefix = 'data:image/png;base64,'
    // let image = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.loadImage(base64Prefix + base64image)
    // let tooBig = image.width > maxSupportedEdge || image.height > maxSupportedEdge;
    // let tooSmall = image.width < minSupportedEdge || image.height < minSupportedEdge;

    // if (tooBig || tooSmall) {
    //   let resizeOptions = MediaImageRepositoryProcessing.defaultResizeOptions
    //   resizeOptions.allowUpsampling = true
    //   base64image = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.resizeBase64ToMaxEdgeSize(image.src, maxSupportedEdge, resizeOptions);
    //   base64image = base64image.replace(base64Prefix, '');
    // }
    let path = '502mou2dpjffnk'
    let params = {
      input: { 
        input_image: base64image,
        upscale_factor,
        req_size: null,
        lambdaSem : 0.75,
        lambdaPix : 0.5
      }
    };
    return this.runpod(path, params)
  }

  replicate(
    params: any,
    abortSignal?: AbortSignal,
    polling: { maxAttempts?: number; intervalMs?: number } = {},
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const { maxAttempts = 10, intervalMs = 2000 } = polling
        const config = {
          headers: {
            // 'content-type': 'multipart/form-data',
            "Content-Type": "application/json; charset=utf-8",
            "Accept": "application/json",
          },
          maxRedirects: 10,
          signal: abortSignal
        }
        const data = await this.api.post(`/replicate`, params, config)

        let completed;
        for (let i = 0; i < maxAttempts; i++) {
          // Check if the operation was aborted
          if (abortSignal?.aborted) {
            reject(new Error('Operation aborted'));
            return;
          }
          
          // @ts-ignore
          const latest = await this.api.get(`/replicate/${data.data.id}`, config)
          // @ts-ignore
          if (latest.data.status !== "starting" && latest.data.status !== "processing") {
            completed = latest;
            break;
          }
          // Wait for 2 seconds and then try again.
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }

        if (completed == undefined) {
          throw new Error('Replicate prediction timed out');
        }
        // @ts-ignore
        if (completed.data.status === 'failed' || completed.data.status === 'canceled') {
          // @ts-ignore
          throw new Error(completed.data.error ?? `Replicate prediction ${completed.data.status}`)
        }
        resolve(completed.data.output)
      } catch (err) {
        reject(err)
      }
    })
  }

  // Long-polling variant for slow Replicate models (e.g. video generation).
  // Default budget: 180 attempts × 3s ≈ 9 minutes (Seedance video can take 30–120s, but
  // 1080p with longer durations can spike higher).
  replicateLong(
    params: any,
    options?: { abortSignal?: AbortSignal; maxAttempts?: number; intervalMs?: number },
  ): Promise<any> {
    const { abortSignal, maxAttempts = 180, intervalMs = 3000 } = options ?? {}
    return new Promise(async (resolve, reject) => {
      try {
        const config = {
          headers: {
            "Content-Type": "application/json; charset=utf-8",
            "Accept": "application/json",
          },
          maxRedirects: 10,
          signal: abortSignal,
        }
        const data = await this.api.post(`/replicate?model=video&length=${params?.input?.duration}&resolution=${params?.input?.resolution}`, params, config)

        let completed
        for (let i = 0; i < maxAttempts; i++) {
          if (abortSignal?.aborted) {
            reject(new Error('Operation aborted'))
            return
          }
          // @ts-ignore
          const latest = await this.api.get(`/replicate/${data.data.id}`, config)
          // @ts-ignore
          if (latest.data.status !== "starting" && latest.data.status !== "processing") {
            completed = latest
            break
          }
          await new Promise(resolve => setTimeout(resolve, intervalMs))
        }

        if (completed == undefined) {
          throw 'Time out'
        }
        // @ts-ignore
        if (completed.data.status === 'failed' || completed.data.status === 'canceled') {
          // @ts-ignore
          throw completed.data.error ?? `Replicate prediction ${completed.data.status}`
        }
        resolve(completed.data.output)
      } catch (err) {
        reject(err)
      }
    })
  }

  // Seedance 1 Pro image-to-video generation via Replicate.
  // Mirrors iOS `EditWithAIAPIManager.generateVideo` (Seedance 1.5 Pro, no audio).
  seedanceVideo(
    prompt: string,
    imageDataUrl: string,
    options?: {
      resolution?: '480p' | '720p' | '1080p'
      duration?: number
      aspectRatio?: string
      abortSignal?: AbortSignal
    },
  ): Promise<string | null> {
    const {
      resolution = '480p',
      duration = 4,
      aspectRatio = '16:9',
      abortSignal,
    } = options ?? {}
    const params = {
      version: 'bytedance/seedance-1.5-pro',
      input: {
        prompt,
        image: imageDataUrl,
        resolution,
        duration,
        aspect_ratio: aspectRatio,
        generate_audio: false,
      },
    }
    return this.replicateLong(params, { abortSignal }).then(output => {
      // Seedance returns either a string URL or { video: url } depending on backend wrapping.
      if (typeof output === 'string') return output
      if (output && typeof output === 'object') {
        if (typeof output.video === 'string') return output.video
        if (Array.isArray(output) && typeof output[0] === 'string') return output[0]
      }
      return null
    })
  }

  getContentTranslation(): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/localization/`)
        if (data?.objects?.length > 0 && data?.objects[0].localizations)
          resolve(data.objects[0].localizations)
      } catch (err) {
        reject(err)
      }
    })
  }

  inpaintImageUnauth(formData): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        customAmplitude('Magic Heal')
        const config = {
          headers: {
            'content-type': 'multipart/form-data',
          },
          maxRedirects: 10
        }

        const data = await this.api.post('/magic_heal/', formData, config)
        resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }

  async captionImageUnauth(base64image: any): Promise<string[]> {
    let path = 'caption_image'
    let params = {
      input: {
        "questions": ["describe the image in one noun", "describe what’s in the image", "is this a photo of a person?"],
        "data_url": base64image,
        "task": "visual_question_answering"
      }
    };

    let result = await this.runpodUnauth(path, params)
    return result["results"].map(r => r["answer"] )[0]
  }

  magicBgUnauth(base64image: any, base64Mask: any, isProduct: boolean, prompt: string): Promise<any> {
    let path = 'magic_background_v2'
    let params = {
      input: { 
        negative_prompt: "text, (((watermark))), low quality, ugly, deformed, boring, bad quality, cartoon, ((disfigured)), ((bad art)), ((deformed)), ((poorly drawn)), ((extra limbs)), ((close up)), ((b&w)), weird colors, blurry, ugly, tiling, poorly drawn hands, poorly drawn feet, poorly drawn face, out of frame, extra limbs, disfigured, deformed, body out of frame, blurry, bad anatomy, blurred, watermark, grainy, signature, cut off, draft, low detail, low quality, double face, 2 faces, cropped, ugly, low-res, tiling, grainy, cropped, ostentatious, ugly, oversaturated, grain, low resolution, disfigured, blurry, bad anatomy, disfigured, poorly drawn face, mutant, mutated, extra limb, ugly, poorly drawn hands, missing limbs, blurred, floating limbs, disjointed limbs, deformed hands, blurred, out of focus, long neck, long body, ugly, disgusting, childish, cut off cropped, distorted, imperfect, surreal, bad hands, text, error, extra digit, fewer digits, cropped , worst quality, low quality, normal quality, jpeg artifacts, signature, watermark, username, blurry, artist name, Lots of hands, extra limbs, extra fingers, conjoined fingers, deformed fingers, old, ugly eyes, imperfect eyes, skewed eyes , unnatural face, stiff face, stiff body, unbalanced body, unnatural body, lacking body, details are not clear, cluttered, details are sticky, details are low, distorted details, ugly hands, imperfect hands, (mutated hands and fingers:1.5), (long body :1.3), (mutation, poorly drawn :1.2) bad hands, fused ha nd, missing hand, disappearing arms, hands, disappearing thigh, disappearing calf, disappearing legs, ui, missing fingers",
        prompt: prompt,
        number_of_images: 1,
        is_product: isProduct,
        input_image: base64image,
        mask: base64Mask
      }
    };
    return this.runpodUnauth(path, params).then(r => r['magic_background_results'])
  }

  async ideogram(prompt: string, aspectRatio: string, options?: {
    styleType?: string;
    magicPromptOption?: string;
    abortSignal?: AbortSignal;
  }): Promise<any> {
    const {
      styleType = 'Auto', // \"None\", \"Auto\", \"General\", \"Realistic\", \"Design\""
      magicPromptOption = 'Auto',
      abortSignal
    } = options || {};
    
    const params = {
      version: "ideogram-ai/ideogram-v3-turbo",
      input: {
        prompt: prompt,
        aspect_ratio: aspectRatio,
        style_type: styleType,
        magic_prompt_option: magicPromptOption
      }
    };
    return this.replicate(params, abortSignal);
  }

  openApiWithImage(
    prompt: string,
    base64image?: any,
    abortSignal?: AbortSignal,
    responseFormat?: OpenAiResponseFormat
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const config = {
          maxRedirects: 10,
          signal: abortSignal
        }
        
        let content: any = [
          // {
          //   "type": "text",
          //   "text": prompt
          // }
        ]
        
        if (base64image){
          content.push({
            "type": "image_url",
            "image_url": {
              "url": base64image,
              "detail": "high",
            }
          });
        }

        let params = {
          "model": "gpt-4.1-nano",
          ...(responseFormat ? { "response_format": responseFormat } : {}),
          "messages": [
            {
              "role": "system",
              "content": `You are an expert image editor. ${prompt}`
            },
            {
              "role": "user",
              "content": content
            }
          ]
        }

        
        
        const data = await this.api.post('/openai_completions/', params, config)
        resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }

  openApiEditWithImage(prompt: string, base64image: string | string[], base64mask: any, size: string, quality: AI_STYLE_QUALITY, abortSignal?: AbortSignal): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const config = {
          maxRedirects: 10,
          signal: abortSignal
        }
        
        let formData = new FormData()

        const hasMask = appendOpenAiEditMaskToFormData(formData, base64mask, {
          dataUrlToBlobPreservingMime: this.dataUrlToBlobPreservingMime.bind(this),
          fileExtensionFromMime: this.fileExtensionFromMime.bind(this),
        })
        const model = hasMask ? 'gpt-image-1.5' : 'gpt-image-2'
        const background = hasMask ? 'transparent' : 'auto'

        formData.append('model', model)
        formData.append('prompt', prompt)
        formData.append('size', size ? size : '1024x1024')
        formData.append('quality', quality)
        formData.append('background', background)
        if (hasMask) {
          formData.append('input_fidelity', 'high')
        }
        
        // Handle single image or array of images
        if (Array.isArray(base64image)) {
          base64image.forEach(imageData => {
            const image = this.dataUrlToBlobPreservingMime(imageData)
            const extension = this.fileExtensionFromMime(image.type)
            formData.append('image[]', image, `image.${extension}`)
          })
        } else {
          const image = this.dataUrlToBlobPreservingMime(base64image)
          const extension = this.fileExtensionFromMime(image.type)
          formData.append('image[]', image, `image.${extension}`)
        }

        const { data } = await this.api.post('/images-edits/', formData, config)
        
        resolve({success: true, result: data });
        
      } catch (err) {
        reject(err)
      }
    })
  }

  gemini(
    prompt: string,
    base64image: string | string[],
    abortSignal?: AbortSignal,
    options?: { model?: string; responseModalities?: string[]; aspectRatio?: string; imageSize?: string }
  ): Promise<{success: boolean, result?: any}> {
    return new Promise(async (resolve, reject) => {
      try {
        const config = {
          maxRedirects: 10,
          signal: abortSignal
        }
        const parts = buildGeminiParts(prompt, base64image, this.parseDataUrl.bind(this))
        const params: GeminiGenerateParams = {
          contents: [
            {
              parts,
              role: "user"
            }
          ],
          generationConfig: buildGeminiGenerationConfig(options?.responseModalities || ["Text", "Image"], options?.aspectRatio, options?.imageSize)
        }
        const geminiModel = options?.model || 'gemini-2.5-flash-image'
        const response = await this.api.post(`/gemini_ai/${geminiModel}:generateContent`, params, config)
        let candidates = response.data.candidates[0];
        const finishReason = candidates.finishReason;
        
        const blockedFinishReasons =  ["IMAGE_SAFETY", "SAFETY", "OTHER", "BLOCKLIST", "PROHIBITED_CONTENT"]
        if (blockedFinishReasons.indexOf(finishReason) != -1) {
          resolve({success: false });
          return
        }
        const content = extractGeminiContentFromApiResponse(response.data) || candidates.content
        resolve({success: true, result: content });
      } catch (err) {
        reject(err)
      }
    })
  }

  generateImage(
    prompt: string,
    images: string | string[],
    model: string,
    abortSignal?: AbortSignal,
    aspectRatio?: string,
  ): Promise<{success: boolean, result?: any}> {
    return new Promise(async (resolve, reject) => {
      try {
        const config = {
          maxRedirects: 10,
          signal: abortSignal
        }
        const imageArray = Array.isArray(images) ? images : [images]
        const params: Record<string, any> = { prompt, images: imageArray, model }
        if (aspectRatio) {
          params.aspectRatio = aspectRatio
        }
        const response = await this.api.post('/ai/generate-image', params, config)
        const image = response.data?.image
        if (!image) {
          resolve({success: false });
          return
        }
        resolve({success: true, result: image });
      } catch (err) {
        reject(err)
      }
    })
  }

  async imageEditSuggestionsGemini(base64image: string | string[], promptRef: string, language: string = 'en', abortSignal?: AbortSignal): Promise<SuggestionItem[]> {
    try {
      const config = {
        maxRedirects: 10,
        signal: abortSignal
      }
      const formData = new FormData()
      formData.append('promptRef', promptRef)
      const images = Array.isArray(base64image) ? base64image : [base64image]
      images.forEach((imageData) => {
        const imageBlob = this.dataUrlToBlobPreservingMime(imageData)
        const extension = this.fileExtensionFromMime(imageBlob.type)
        formData.append('image', imageBlob, `image.${extension}`)
      })
      formData.append('language', language)

      const { data } = await this.api.post('/ai/edit-suggestion/', formData, config)
      const responseContent = extractGeminiContentFromApiResponse(data)
      if (!responseContent) {
        return []
      }

      const textResponse = extractGeminiText(responseContent)
      return this.parseSuggestionItems(textResponse, 'Failed to parse Gemini image edit suggestions:')
    } catch (error) {
      if (axios.isCancel(error) || (error as any)?.code === 'ERR_CANCELED') {
        throw error
      }
      console.error('Failed to fetch Gemini image edit suggestions:', error)
      return []
    }
  }


  runpodUnauth(path: string, params: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const config = {
          headers: {
            // 'content-type': 'multipart/form-data',
            "Content-Type": "application/json; charset=utf-8",
            "Accept": "application/json",
          },
          maxRedirects: 10,
        }
        const data = await this.api.post(`/${path}/run`, params, config)

        const completed = await this.pollUntilTerminal(
          () => this.api.get(`/${path}/status/${data.data.id}`, config),
          (latest: any) => latest.data.status,
          1000
        )

        if (!['completed', 'failed'].includes(String((completed as any)?.data?.status).toLowerCase())) {
          customAmplitude('Magic Backgrounds Error')
          throw 'Time out';
        }
        customAmplitude('Magic Backgrounds Start')

        resolve((completed as any).data.output)
      } catch (err) {
        reject(err)
      }
    })
  }

  async captionImage(base64image: any): Promise<string[]> {
    let questions = ["describe the image in one noun", "describe what’s in the image", "is this a photo of a person?"];
    return this.askQuestion(base64image, questions);
  }

  async askQuestion(base64image: any, questions: string[], abortSignal?: AbortSignal): Promise<string[]> {
    const config = { maxRedirects: 10, signal: abortSignal }

    const imageBlob = this.dataUrlToBlobPreservingMime(base64image)
    const formData = new FormData()
    formData.append('task', 'blip')
    formData.append('image_quality', '100')
    questions.forEach(question => formData.append('image_query', question))
    formData.append('image', imageBlob, `image.${this.fileExtensionFromMime(imageBlob.type)}`)

    const initialJob = (await this.api.post('/ai/image-processing', formData, config)).data
    const job = ['completed', 'failed'].includes(String(initialJob?.status).toLowerCase())
      ? initialJob
      : await this.pollUntilTerminal(
          async () => (await this.api.get(`/ai/image-processing/${initialJob.id}`, config)).data,
          (item: any) => item?.status,
          1000
        )

    const answers = job?.output?.answers ?? job?.output?.results ?? job?.output?.result ?? []
    return answers.map((answer: any) => answer?.answer ?? answer ?? '')
  }

  magicBg(base64image: any, base64Mask: any, isProduct: boolean, prompt: string, abortSignal?): Promise<any> {
    let path = '83kmw56zqfjdzl'
    let params = {
      input: { 
        negative_prompt: "text, (((watermark))), low quality, ugly, deformed, boring, bad quality, cartoon, ((disfigured)), ((bad art)), ((deformed)), ((poorly drawn)), ((extra limbs)), ((close up)), ((b&w)), weird colors, blurry, ugly, tiling, poorly drawn hands, poorly drawn feet, poorly drawn face, out of frame, extra limbs, disfigured, deformed, body out of frame, blurry, bad anatomy, blurred, watermark, grainy, signature, cut off, draft, low detail, low quality, double face, 2 faces, cropped, ugly, low-res, tiling, grainy, cropped, ostentatious, ugly, oversaturated, grain, low resolution, disfigured, blurry, bad anatomy, disfigured, poorly drawn face, mutant, mutated, extra limb, ugly, poorly drawn hands, missing limbs, blurred, floating limbs, disjointed limbs, deformed hands, blurred, out of focus, long neck, long body, ugly, disgusting, childish, cut off cropped, distorted, imperfect, surreal, bad hands, text, error, extra digit, fewer digits, cropped , worst quality, low quality, normal quality, jpeg artifacts, signature, watermark, username, blurry, artist name, Lots of hands, extra limbs, extra fingers, conjoined fingers, deformed fingers, old, ugly eyes, imperfect eyes, skewed eyes , unnatural face, stiff face, stiff body, unbalanced body, unnatural body, lacking body, details are not clear, cluttered, details are sticky, details are low, distorted details, ugly hands, imperfect hands, (mutated hands and fingers:1.5), (long body :1.3), (mutation, poorly drawn :1.2) bad hands, fused ha nd, missing hand, disappearing arms, hands, disappearing thigh, disappearing calf, disappearing legs, ui, missing fingers",
        prompt: prompt,
        number_of_images: 1,
        is_product: isProduct,
        input_image: base64image,
        mask: base64Mask
      }
    };
    return this.runpod(path, params, abortSignal).then(r => r['magic_background_results'])
  }

  runpod(path: string, params: any, abortSignal?): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const config = {
          headers: {
            // 'content-type': 'multipart/form-data',
            "Content-Type": "application/json; charset=utf-8",
            "Accept": "application/json",
          },
          maxRedirects: 10,
          signal: abortSignal
        }
        const data = await this.api.post(`/runpod/${path}/run`, params, config)

        const completed = await this.pollUntilTerminal(
          () => this.api.get(`/runpod/${path}/status/${data.data.id}`, config),
          (latest: any) => latest.data.status,
          1500
        )

        if (!['completed', 'failed'].includes(String((completed as any)?.data?.status).toLowerCase())) {
          customAmplitude('Magic Backgrounds Error')
          throw 'Time out';
        }
        customAmplitude('Magic Backgrounds Start')

        resolve((completed as any).data.output)
      } catch (err) {
        reject(err)
      }
    })
  }

  // magic background
  getMagicBgPromptClassifications(): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/aipromptclassification/?category=4&isHidden=true`)
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  getMagicBgPromptsByClassification(classificationId: string): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/magicportraitprompt/?classification=${classificationId}&isHidden=true`)
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  getMagicBgPromptsByClassifications(classificationIds: string[]): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/magicportraitprompt/?classification__in=${classificationIds.toString()}&isHidden=true`)
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  getMagicBgPromptsBySubject(subject: string): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/magicportraitprompt/?subjects__name=${subject}`)
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  getWhatsNew(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/whatsnew/?is_active=true&platforms__name__in=web`)
        resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }

  async aiImage(prompt: string, targetWidth: number, targetHeight: number, abortSignal?: AbortSignal): Promise<any> {
    const path = 'huq45p91k8czpw'
    const base64Prefix = 'data:image/png;base64,'

    // let image = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.createColorImage('#ffffff', targetWidth, targetHeight);
    // let imageInBase64 = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.blobUrlToBase64(image);
    // let mask = await  ImageJs.load(image);
    // let maskInBase64 = mask.grey().toDataURL();

    let params = {
      input: {
        // img: imageInBase64.replace(base64Prefix, ''),
        // mask_img: maskInBase64.replace(base64Prefix, ''),
        negative_prompt: "text, (((watermark))), low quality, ugly, deformed, boring, bad quality, cartoon, ((disfigured)), ((bad art)), ((deformed)), ((poorly drawn)), ((extra limbs)), ((close up)), ((b&w)), weird colors, blurry, ugly, tiling, poorly drawn hands, poorly drawn feet, poorly drawn face, out of frame, extra limbs, disfigured, deformed, body out of frame, blurry, bad anatomy, blurred, watermark, grainy, signature, cut off, draft, low detail, low quality, double face, 2 faces, cropped, ugly, low-res, tiling, grainy, cropped, ostentatious, ugly, oversaturated, grain, low resolution, disfigured, blurry, bad anatomy, disfigured, poorly drawn face, mutant, mutated, extra limb, ugly, poorly drawn hands, missing limbs, blurred, floating limbs, disjointed limbs, deformed hands, blurred, out of focus, long neck, long body, ugly, disgusting, childish, cut off cropped, distorted, imperfect, surreal, bad hands, text, error, extra digit, fewer digits, cropped , worst quality, low quality, normal quality, jpeg artifacts, signature, watermark, username, blurry, artist name, Lots of hands, extra limbs, extra fingers, conjoined fingers, deformed fingers, old, ugly eyes, imperfect eyes, skewed eyes , unnatural face, stiff face, stiff body, unbalanced body, unnatural body, lacking body, details are not clear, cluttered, details are sticky, details are low, distorted details, ugly hands, imperfect hands, (mutated hands and fingers:1.5), (long body :1.3), (mutation, poorly drawn :1.2) bad hands, fused hand, missing hand, disappearing arms, hands, disappearing thigh, disappearing calf, disappearing legs, ui, missing fingers",
        prompt: prompt,
        width: targetWidth,
        height: targetHeight,
        num_inference_steps: 3,
        strength: 1.0
      }
    };
    let output = await this.runpod(path, params, abortSignal)
    return base64Prefix + output[0]
  }

  // magic templates
  /**
   * Get AI prompt categories by type (e.g., "magic_templates")
   * This is the first step in the magic templates flow
   */
  getAIPromptCategories(type: string): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/aipromptcategory/?type=${type}`)
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Get AI prompt classifications by category ID
   * This is the second step in the magic templates flow
   */
  getAIPromptClassificationsByCategory(superCategories: string[]): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/aipromptclassification/?category__subtype__in=${superCategories.toString()}`)
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Get AI prompt classifications by category type
   */
  getAIPromptClassificationsByType(type: string): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/aipromptclassification/?category__type__in=${encodeURIComponent(type)}`)
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Get magic prompts by classification ID
   * Uses the /magicportraitprompt/ endpoint
   */
  getMagicPromptsByClassification(classificationId: string | number): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await this.api.get(`/magicportraitprompt/?classification=${classificationId}&limit=100&isHidden=true`)
        resolve(data.objects)
      } catch (err) {
        reject(err)
      }
    })
  }

  private async getAIGridModelsByType(type: string): Promise<{
    aspectRatio: number,
    data: LabeledImageItem[],
    classifications: ClassificationGroup[]
  }> {
    const rawClassifications = await this.getAIPromptClassificationsByType(type)
    const classificationItems = await Promise.all(
      rawClassifications.map((classification: any) =>
        this.getMagicPromptsByClassification(classification.id)
      )
    )

    const mapItem = (item: any): LabeledImageItem[] => {
      const src = item.assets?.[0]?.file
      const title = item.title
      if (!src || !title) return []
      const result: LabeledImageItem = { src, title }
      if (item.prompt) result.prompt = item.prompt
      if (item.gender) result.gender = item.gender
      if (item.age_category) result.age = item.age_category
      return [result]
    }

    const classifications: ClassificationGroup[] = rawClassifications.map((c: any, idx: number) => ({
      id: c.id,
      title: c.title,
      items: classificationItems[idx].flatMap(mapItem),
    }))

    const data = classifications.flatMap(c => c.items)

    // Skip aspect_ratio: 0 from CMS — falsy values collapse non-image grid tiles.
    const aspectRatio =
      rawClassifications
        .map((c: any) => c.aspect_ratio)
        .find((ratio: unknown): ratio is number => typeof ratio === 'number' && ratio > 0)
      ?? 1

    return { aspectRatio, data, classifications }
  }

  async getAIFashionModels() { return this.getAIGridModelsByType('ai_models') }

  async getAIBackgroundModels() { return this.getAIGridModelsByType('ai_models_backgrounds') }

  /**
   * Get all magic templates data following the iOS flow:
   * 1. Get super categories via /aipromptcategory/?type=magic_templates
   * 2. For each super category, get classifications via /aipromptclassification/?category=<id>
   * 3. For each classification, get prompts via /magicportraitprompt/?classification=<id>
   */
  async getMagicTemplatesData(superCategories: string[]): Promise<any[]> {
    try {
      const classifications = await this.getAIPromptClassificationsByCategory(superCategories)

      const classificationsWithData = await Promise.all(
        classifications.map(async (classification) => {
          const data = await this.getMagicPromptsByClassification(classification.id)
          return { ...classification, data }
        })
      )

      return classificationsWithData
    } catch (err) {
      throw err
    }
  }

  // ─── Home feed (iOS HomeFeedManager parity) ───────────────────────────────
  /**
   * Fetch the ordered home-feed section definitions from `/home_feed_section/`.
   * Mirrors iOS `homeFeedSection.list`. Each object carries fully embedded
   * `aiClassification` / `templateCategory` objects, so the web does NOT need
   * iOS's per-section metadata re-fetch — only item lists are loaded on resolve.
   */
  getHomeFeedSections(showHidden: boolean = false): Promise<HomeFeedSectionRaw[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const hiddenParam = showHidden ? '&isHidden=true' : ''
        const { data } = await this.api.get(`/home_feed_section/?offset=0&limit=100${hiddenParam}`)
        resolve(Array.isArray(data?.objects) ? data.objects : [])
      } catch (err) {
        reject(err)
      }
    })
  }

  /**
   * Resolve the ordered home-feed into per-type sections with their item lists.
   * Sorts by `order` then `id`, filters hidden sections, drops empty and regular
   * video-template sections, and falls back to a client-side AI feed if the
   * endpoint is unavailable (401/404) — so the feed page never renders blank.
   */
  async resolveHomeFeedSections(showHidden: boolean = false): Promise<HomeFeedResolvedSection[]> {
    let raw: HomeFeedSectionRaw[]
    try {
      raw = await this.getHomeFeedSections(showHidden)
    } catch (err) {
      console.warn('home_feed_section unavailable, using fallback feed:', err)
      return this.resolveHomeFeedFallbackSections()
    }

    const ordered = [...raw]
      .filter(section => showHidden || !(section.is_hidden ?? section.isHidden))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id)

    // Resolve each section independently: one classification whose item fetch
    // fails must not collapse the whole feed, so failures drop to null and the
    // remaining sections still render.
    const resolved = await Promise.all(
      ordered.map(section =>
        this.resolveHomeFeedSection(section).catch(err => {
          console.warn(`home feed section ${section.id} failed to resolve:`, err)
          return null
        }),
      ),
    )
    const nonEmpty = resolved.filter(
      (section): section is HomeFeedResolvedSection => section !== null && section.items.length > 0,
    )

    return nonEmpty.length ? nonEmpty : this.resolveHomeFeedFallbackSections()
  }

  private async resolveHomeFeedSection(
    section: HomeFeedSectionRaw,
  ): Promise<HomeFeedResolvedSection | null> {
    const aiClassification = section.ai_classification ?? section.aiClassification
    const templateCategory = section.template_category ?? section.templateCategory

    // Section type = whichever embedded object is present (skip if both/neither).
    if (aiClassification && !templateCategory) {
      const classificationId = Number(aiClassification.id)
      if (!Number.isFinite(classificationId)) return null
      const rawItems = await this.getMagicPromptsByClassification(classificationId)
      const items = rawItems.slice(0, HOME_FEED_AI_PREVIEW_MAX).map(mapHomeFeedAiItem)
      return {
        kind: resolveAiSectionKind(classificationId, aiClassification, items),
        id: classificationId,
        title: firstNonEmptyTitle(section.title, aiClassification.title, aiClassification.short_title),
        order: section.order ?? aiClassification.order ?? 0,
        aspectRatio: aiClassification.aspect_ratio ?? aiClassification.aspectRatio ?? 0,
        items,
      }
    }

    if (templateCategory && !aiClassification) {
      // Regular video template categories are intentionally excluded.
      if (isVideoTemplateCategory(templateCategory)) return null
      const categoryId = Number(templateCategory.id)
      if (!Number.isFinite(categoryId)) return null
      const rawItems = await this.getTemplatesByCategory(categoryId)
      const items = rawItems
        .map(mapHomeFeedTemplateItem)
        .filter((item): item is HomeFeedTemplateItem => item !== null)
      return {
        kind: 'template',
        id: categoryId,
        title: firstNonEmptyTitle(section.title, templateCategory.name, templateCategory.title),
        order: section.order ?? 0,
        items,
      }
    }

    return null
  }

  /**
   * Client-side fallback feed when `/home_feed_section/` is unavailable: reuses
   * the `business` AI classifications so the AI templates rows still render.
   */
  private async resolveHomeFeedFallbackSections(): Promise<HomeFeedResolvedSection[]> {
    try {
      const filters = await this.getMagicTemplatesData(['business'])
      return filters
        .filter(Boolean)
        .map((filter: any, index: number): HomeFeedResolvedSection => {
          const id = Number(filter.id)
          const items = (filter.data ?? []).slice(0, HOME_FEED_AI_PREVIEW_MAX).map(mapHomeFeedAiItem)
          return {
            kind: resolveAiSectionKind(id, filter, items),
            id,
            title: filter.title ?? '',
            order: index,
            aspectRatio: filter.aspect_ratio ?? 0,
            items,
          }
        })
        .filter(section => section.items.length > 0)
    } catch (err) {
      console.error('home_feed_section fallback failed:', err)
      return []
    }
  }

  /**
   * The AI-video section for the create-page video row. AI-video classifications
   * (113/132, plus newer ones like 142/143) live under the dedicated
   * `ai_videos` category type — not the `business` subtype used by "Try
   * Templates" — so they're fetched separately via `category__type__in`.
   *
   * The create page shows a single row, so this walks the classifications in
   * backend order and returns the first one that actually has items. Items are
   * fetched one classification at a time rather than for all of them up front,
   * since every fetch beyond the rendered row is wasted.
   */
  async getAiVideoSections(): Promise<HomeFeedAiSection[]> {
    try {
      const classifications = await this.getAIPromptClassificationsByType(AI_VIDEO_CATEGORY_TYPE)
      const ordered = [...classifications]
        .map((classification: any, index: number) => ({ classification, index }))
        .sort((a, b) => (a.classification.order ?? a.index) - (b.classification.order ?? b.index))

      for (const { classification, index } of ordered) {
        const classificationId = Number(classification.id)
        if (!Number.isFinite(classificationId)) continue
        let rawItems: any[]
        try {
          rawItems = await this.getMagicPromptsByClassification(classificationId)
        } catch (err) {
          console.warn(`AI-video classification ${classificationId} failed to load:`, err)
          continue
        }
        const items = rawItems.slice(0, HOME_FEED_AI_PREVIEW_MAX).map(mapHomeFeedAiItem)
        if (!items.length) continue
        return [
          {
            kind: 'ai-video',
            id: classificationId,
            title: classification.title ?? '',
            order: classification.order ?? index,
            aspectRatio: classification.aspect_ratio ?? 0,
            items,
          },
        ]
      }
      return []
    } catch (err) {
      console.error('Failed to load AI-video sections:', err)
      return []
    }
  }

  fluxKontextPro(prompt: string, base64image: string, abortSignal?: AbortSignal): Promise<any> {
    const params = {
      version: "black-forest-labs/flux-kontext-pro",
      input: {
        input_image: base64image,
        prompt: prompt,
        output_format: "jpg"
      }
    };
    return this.replicate(params);
  }

  async flux_kontext(prompt: string, base64image: any, abortSignal?: AbortSignal): Promise<any> {
    const params = {
      input: {
        prompt: prompt,
        input_image: base64image,
        output_format: "jpg",
      }
    };
    
    return new Promise(async (resolve, reject) => {
      try {
        const config = {
          headers: {
            // 'content-type': 'multipart/form-data',
            "Content-Type": "application/json; charset=utf-8",
            "Accept": "application/json",
          },
          maxRedirects: 10,
        }
        const data = await this.api.post(`/flux_kontext/`, params, config)

        let completed;
        for (let i = 0; i < 10; i++) {
          // @ts-ignore
          const latest = await this.api.get(`/replicate/${data.data.id}`, config)
          // @ts-ignore
          if (latest.data.status !== "starting" && latest.data.status !== "processing") {
            completed = latest;
            break;
          }
          // Wait for 2 seconds and then try again.
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        if (completed == undefined) {
          throw 'Time out';
        }
        resolve({success: true, result: completed.data.output });
      } catch (err) {
        reject(err)
      }
    })
  }


  magicDesignImageReplacement(image: string, originalImages: string[]): Promise<string> {
    let path = 'a838onl6ozmd1g' // magic-design-4-image-replacement
    let params = {
      input: {
        template: image,
        original_images: originalImages
      }
    };

    return this.runpod(path, params).then(output => output.result);
  }

  magicDesignTextReplacement(image: string): Promise<MagicDesignTextInfo[]> {
    let path = 'gb3g5kbu048fp8' // magic-design-3-text-extraction
    let params = {
      input: {
        input_image: image,
      }
    };

    /**
     * Convert the server structure to the client format.
     *
     * @param {Object} data
     * @param {Array<Array<number>>} data.box_coordinates – four [x, y] pairs
     * @param {Array<number>} data.text_color – [r, g, b]
     * @returns {{box_coordinates: {x:number,y:number,width:number,height:number}, text_color: string}}
     */
    const convertAnnotation = (data: any): MagicDesignTextInfo => {
      // --- box -------------------------------------------------
      const xs = data.box_coordinates.map(pt => pt[0]);
      const ys = data.box_coordinates.map(pt => pt[1]);

      const x = Math.min(...xs);
      const y = Math.min(...ys);
      const width  = Math.max(...xs) - x;
      const height = Math.max(...ys) - y;

      // --- color ----------------------------------------------
      const hex = c => c.toString(16).padStart(2, '0').toUpperCase();
      const [r, g, b] = data.text_color;
      const text_color = `#${hex(r)}${hex(g)}${hex(b)}`;

      let magicDesignTextInfo = new MagicDesignTextInfo();
      magicDesignTextInfo.box_coordinates = new Rectangle(x, y, width, height)
      magicDesignTextInfo.box_text = data["box_text"]
      magicDesignTextInfo.box_angle = data["box_angle"]
      magicDesignTextInfo.font_name = data["font_name"]
      magicDesignTextInfo.has_shadow = data["has_shadow"]
      magicDesignTextInfo.has_outline = data["has_outline"]
      magicDesignTextInfo.shadow_kernel_class = data["shadow_kernel_class"]
      magicDesignTextInfo.shadow_opacity_class = data["shadow_opacity_class"]
      magicDesignTextInfo.shadow_direction_class = data["shadow_direction_class"]
      magicDesignTextInfo.shadow_amplitude = data["shadow_amplitude"]
      magicDesignTextInfo.outline_color = data["outline_color"]
      magicDesignTextInfo.outline_width = data["outline_width"]
      magicDesignTextInfo.text_color = text_color
      magicDesignTextInfo.font_height = data["font_height"]
      
      return magicDesignTextInfo;
    }
    
    return this.runpod(path, params).then(rslt => {
      if (!rslt?.template_data || !Array.isArray(rslt.template_data)) {
        return [];
      }
      return rslt.template_data.map(data => convertAnnotation(data));
    })
  }
  
  async qwenImage( prompt: string, base64image: string | string[], abortSignal?): Promise<any> {
    let path = '5hueodp09zon0g'

    // Handle single image or array of images - backend expects List[str]
    const inputImages = Array.isArray(base64image) ? base64image : [base64image];

    let params = {
      input: { 
        prompt: prompt,
        input_image: inputImages,  // List[str] as per backend interface
        negative_prompt: null,     // Optional[str] = None
        image_size: null,          // Optional[tuple] = None
        number_of_images: 1,       // Optional[int] = 1
        n_steps: 4,                // Optional[int] = 4
        seed: null,                // Optional[int] = None
        save_quality: 100,         // Optional[int] = 100
      }
    };
    return this.runpod(path, params, abortSignal).then(r => r['results'][0])
  }

  //use like this: api.openAiResponses({promptId:"pmpt_6882839c27b88195a436ec9bfe8a930b0164ba30749b2a4c",base64image: resizedOriginal})
  openAiResponses(params: {
    promptId: string;
    version?: string | null;
    input?: string[];
    inputType?: string;
    maxOutputTokens?: number;
    store?: boolean | null;
    variables?: Record<string, string> | null;
    abortSignal?: AbortSignal;
    base64image?: string;
    suppressGlobalError?: boolean;
  }): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const {
          promptId,
          version = null,
          input = [],
          inputType = 'input_text',
          maxOutputTokens = 2048,
          store = undefined,
          variables = undefined,
          abortSignal,
          base64image,
          suppressGlobalError = false,
        } = params

        const config: any = {
          maxRedirects: 10,
          signal: abortSignal,
          bazaartSuppressGlobalError: suppressGlobalError,
        }

        const inputs = input.map((t) => ({
          role: 'user',
          content: [{ type: inputType, text: t }],
        }))

        if (base64image) {
          inputs.push({
            role: 'user',
            // @ts-ignore
            content: [{ type: 'input_image', image_url: base64image, detail: 'high'}],
          })
        }

        let body: any = {
          prompt: { id: promptId },
          input: inputs,
          max_output_tokens: maxOutputTokens,
        }

        if (version) body.prompt.version = version
        if (variables) body.prompt.variables = variables
        if (store !== undefined) body.store = store

        const safetyIdentifier = await getSafetyIdentifier()
        if (safetyIdentifier) body.safety_identifier = safetyIdentifier

        const { data } = await this.api.post('/openai_responses/', body, config)
        
        // Handle new response format: output[].content[].text where type is "message"
        if (data && data.output && Array.isArray(data.output)) {
          const messageOutput = data.output.find((item: any) => item.type === 'message')
          if (messageOutput && messageOutput.content && Array.isArray(messageOutput.content)) {
            const textContent = messageOutput.content.find((c: any) => c.type === 'output_text')
            if (textContent && textContent.text) {
              resolve(textContent.text)
              return
            }
          }
        }
        
        // Fallback: old format with choices[0].message.content
        if (data && data.choices && data.choices.length > 0) {
          resolve(data.choices[0].message.content)
        } else {
          resolve(null)
        }
        //resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }

  async moderateContent(text: string, base64image: string, abortSignal?: AbortSignal): Promise<any> {
    const formData = new FormData()
    formData.append('text', text)

    const imageBlob = this.dataUrlToBlobPreservingMime(base64image)
    const extension = this.fileExtensionFromMime(imageBlob.type)
    formData.append('image', imageBlob, `moderation.${extension}`)

    const { data } = await this.api.post('/ai/moderation/', formData, { maxRedirects: 10, signal: abortSignal })
    return data
  }

  imageEditSuggestions(base64image: string, language: string = "en", abortSignal?: AbortSignal): Promise<SuggestionItem[]> {
    return this.openAiResponses({
      promptId: "pmpt_69947183d7008194a6a9662413ea42e907c29848eb0aaadf",
      base64image: base64image,
      variables: {
        number_of_suggestions: "5",
        language: language
      },
      abortSignal: abortSignal
    }).then((response) => {
      return this.parseSuggestionItems(response, 'Failed to parse image edit suggestions:')
    })
  }

  backgroundSuggestions(base64image: string, language: string = "en", abortSignal?: AbortSignal): Promise<Array<{title: string, isPlaceholder: boolean, prompt: string, icon: string}>> {
    return this.openAiResponses({
      promptId: "pmpt_6979ddd7a380819396923698fa78e2ea010f9f03b6806c2b",
      base64image: base64image,
      variables: {
        number_of_suggestions: "5",
        language: language
      },
      abortSignal: abortSignal
    }).then((response) => {
      try {
        // Parse the JSON response if it's a string
        const parsed = typeof response === 'string' ? JSON.parse(response) : response
        
        // Handle array of strings - transform to objects
        if (Array.isArray(parsed)) {
          return parsed.map((item: any) => {
            // If item is already an object with title/prompt/icon, add isPlaceholder
            if (typeof item === 'object' && item !== null) {
              return {
                title: item.title || '',
                isPlaceholder: false,
                prompt: item.prompt || '',
                icon: item.icon || ''
              }
            }
            // If item is a string, use it as both title and prompt
            return {
              title: String(item),
              isPlaceholder: false,
              prompt: String(item),
              icon: ''
            }
          })
        }
        
        return []
      } catch (e) {
        console.error('Failed to parse background suggestions:', e)
        return []
      }
    })
  }
  
  private async retryWithBackoff<T>(
    fn: () => Promise<T>,
    abortSignal?: AbortSignal,
    maxAttempts: number = 4,
  ): Promise<T> {
    const initialDelay = 500
    const multiplier = 2
    const maxDelay = 2500
    const jitter = 0.2

    let lastError: any
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn()
      } catch (err: any) {
        if (err?.code === 'ERR_CANCELED' || abortSignal?.aborted) throw err
        lastError = err
        if (attempt < maxAttempts) {
          const exponential = initialDelay * Math.pow(multiplier, attempt - 1)
          const clamped = Math.min(exponential, maxDelay)
          const jittered = clamped * (1 - jitter + Math.random() * 2 * jitter)
          await new Promise(resolve => setTimeout(resolve, jittered))
        }
      }
    }
    throw lastError
  }

  private parseOpenAiJson(response: unknown): any {
    return typeof response === 'string' ? JSON.parse(response) : response
  }

  private mapPoseCompositions(
    response: unknown,
    variants: number,
    errorMessage: string,
  ): Array<{ pose: string; motion: string; camera_angle: string; crop: string }> {
    const parsed = this.parseOpenAiJson(response)
    const compositions: Array<{ pose: string; motion?: string; camera_angle: string; crop: string }> = parsed?.compositions
    if (!Array.isArray(compositions) || compositions.length === 0) {
      throw new Error(errorMessage)
    }
    return compositions.slice(0, variants).map((c) => ({
      pose: c.pose,
      motion: c.motion ?? '',
      camera_angle: c.camera_angle,
      crop: c.crop,
    }))
  }

  async getAIModelsPoseCompositions(
    referenceImageBase64: string,
    variants: number = 4,
    product?: string,
    abortSignal?: AbortSignal
  ): Promise<Array<{ pose: string; motion: string; camera_angle: string; crop: string }>> {
    return this.retryWithBackoff(async () => {
      const response = await this.openAiResponses({
        promptId: 'pmpt_69b459a6f96c819494add4ebe30f13a50bbd9648ac68e54e',
        version: '30',
        base64image: referenceImageBase64,
        variables: {
          variants: String(variants),
          ...(product?.trim() ? { product: product.trim() } : {}),
        },
        abortSignal,
      })
      return this.mapPoseCompositions(response, variants, 'Invalid pose compositions response structure')
    }, abortSignal)
  }

  /**
   * Primary product metadata (iOS `primaryPhotoshootProductMetadata`).
   * Image-only input; v7 returns product, wearable, category, and model gender.
   */
  async getAIModelsPrimaryProductMetadata(
    productImageBase64: string,
    abortSignal?: AbortSignal,
    behavior: AIRequestBehavior = {},
  ): Promise<{
    product: string
    isWearable: boolean
    productCategory: 'apparel' | 'accessory' | 'jewelry' | 'general'
    modelGender: 'female' | 'male' | null
  }> {
    return this.retryWithBackoff(async () => {
      const response = await this.openAiResponses({
        promptId: 'pmpt_6a3262cdf5a8819398b146c5c173c71a06b2aaa85e16896b',
        version: '7',
        base64image: productImageBase64,
        abortSignal,
        suppressGlobalError: behavior.suppressGlobalError,
      })

      const parsed = this.parseOpenAiJson(response)
      const product = typeof parsed?.product === 'string' ? parsed.product.trim() : ''
      if (!product) {
        throw new Error('Invalid AI models primary product metadata response structure')
      }

      const rawCategory = String(parsed?.product_category ?? parsed?.productCategory ?? '').trim()
      const productCategory: 'apparel' | 'accessory' | 'jewelry' | 'general' =
        rawCategory === 'apparel' || rawCategory === 'accessory' || rawCategory === 'jewelry'
          ? rawCategory
          : 'general'

      const rawGender = String(parsed?.model_gender ?? parsed?.modelGender ?? '').trim().toLowerCase()
      const modelGender: 'female' | 'male' | null =
        rawGender === 'female' || rawGender === 'male' ? rawGender : null

      const isWearableRaw = parsed?.is_wearable ?? parsed?.isWearable
      return {
        product,
        isWearable: isWearableRaw !== false,
        productCategory,
        modelGender,
      }
    }, abortSignal, behavior.retry === false ? 1 : 4)
  }

  /**
   * Second AI Models metadata request (iOS `getAIModelsDefaultComposition`).
   * Text-only input keyed on the product description; returns the default
   * pose/camera/crop composition.
   */
  async getAIModelsDefaultComposition(
    product: string,
    abortSignal?: AbortSignal
  ): Promise<{ pose: string; motion: string; camera_angle: string; crop: string }> {
    return this.retryWithBackoff(async () => {
      const response = await this.openAiResponses({
        promptId: 'pmpt_6a326f5d91fc819782954a27781c1ccb0379641d8e8e42ac',
        version: '8',
        input: [''],
        variables: { product: product.trim() },
        abortSignal,
      })

      const parsed = typeof response === 'string' ? JSON.parse(response) : response
      // iOS decodes this response directly as an AIModelsPoseComposition: the
      // pose/camera_angle/crop keys are top-level, not nested under `composition`.
      if (!parsed?.pose || !parsed?.camera_angle || !parsed?.crop) {
        throw new Error('Invalid AI models default composition response structure')
      }
      return {
        pose: parsed.pose,
        motion: parsed.motion ?? '',
        camera_angle: parsed.camera_angle,
        crop: parsed.crop,
      }
    }, abortSignal)
  }

  /**
   * Combined AI Models prompt metadata (iOS `getAIModelsPromptMetadata`):
   * runs the primary product request first, then the default composition
   * request keyed on the resolved product. `instruction` is the static
   * default; the composition only carries pose/camera_angle/crop.
   */
  async getAIModelsPromptMetadata(
    productImageBase64: string,
    abortSignal?: AbortSignal
  ): Promise<{ product?: string; isWearable: boolean; instruction: string; composition: { pose: string; camera_angle: string; crop: string } }> {
    const primary = await this.getAIModelsPrimaryProductMetadata(productImageBase64, abortSignal)
    const composition = await this.getAIModelsDefaultComposition(primary.product, abortSignal)
    return {
      product: primary.product || undefined,
      isWearable: primary.isWearable,
      instruction: buildDefaultInstruction(1, 2),
      composition: {
        pose: composition.pose,
        camera_angle: composition.camera_angle,
        crop: composition.crop,
      },
    }
  }

  /**
   * Photoshoot hosted-studio shot plan (iOS `getAIPhotoshootShotPlan`).
   * `{ product, variants }` plus the product image → `{ shots: [{ background?, composition }] }`.
   */
  async getPhotoshootShotPlan(
    product: string,
    variants: number,
    productImageBase64: string,
    abortSignal?: AbortSignal,
    prompt?: { id: string; version: string },
  ): Promise<Array<{ background: string | null; composition: string }>> {
    const trimmedProduct = product.trim()
    if (!trimmedProduct) {
      throw new Error('Invalid photoshoot shot plan response structure')
    }
    return this.retryWithBackoff(async () => {
      const response = await this.openAiResponses({
        promptId: prompt?.id ?? 'pmpt_6a4e0b1822648195aabcf5f602110e9a03855032542ce29e',
        version: prompt?.version ?? '33',
        input: [''],
        base64image: productImageBase64,
        variables: {
          product: trimmedProduct,
          variants: String(variants),
        },
        abortSignal,
      })

      const parsed = this.parseOpenAiJson(response)
      const shots: Array<{ background?: string | null; composition?: string }> = parsed?.shots
      if (!Array.isArray(shots) || shots.length === 0) {
        throw new Error('Invalid photoshoot shot plan response structure')
      }
      return shots.slice(0, variants).map((shot) => ({
        background: typeof shot?.background === 'string' ? shot.background.trim() || null : null,
        composition: typeof shot?.composition === 'string' ? shot.composition.trim() : '',
      }))
    }, abortSignal)
  }

  /**
   * Studio-only 9-shot plan for Batch AI Studio In studio. Same JSON shape as
   * `getPhotoshootShotPlan`; the prompt id is `STUDIO_ONLY_SHOT_PLAN_PROMPT_ID`.
   */
  async getStudioOnlyShotPlan(
    product: string,
    variants: number,
    productImageBase64: string,
    abortSignal?: AbortSignal,
  ): Promise<Array<{ background: string | null; composition: string }>> {
    return this.getPhotoshootShotPlan(product, variants, productImageBase64, abortSignal, {
      id: STUDIO_ONLY_SHOT_PLAN_PROMPT_ID,
      version: STUDIO_ONLY_SHOT_PLAN_PROMPT_VERSION,
    })
  }

  /**
   * Photoshoot pose compositions (iOS `getAIPhotoshootPoseCompositions`).
   * Text-only — unlike `getAIModelsPoseCompositions`, this must not send an image.
   */
  async getPhotoshootPoseCompositions(
    variants: number,
    product?: string,
    abortSignal?: AbortSignal,
  ): Promise<Array<{ pose: string; motion: string; camera_angle: string; crop: string }>> {
    return this.retryWithBackoff(async () => {
      const response = await this.openAiResponses({
        promptId: 'pmpt_6a58d54f618081978ce88c0edd4cb9e20a3ad10b788f9de0',
        version: '2',
        input: [''],
        variables: {
          variants: String(variants),
          ...(product?.trim() ? { product: product.trim() } : {}),
        },
        abortSignal,
      })

      return this.mapPoseCompositions(response, variants, 'Invalid photoshoot pose compositions response structure')
    }, abortSignal)
  }

  /**
   * Photoshoot on-model background plan (iOS `getAIPhotoshootModelBackgroundPlan`).
   * `{ product, variants, backgrounds }` plus the product image → `{ shots: [{ background }] }`.
   */
  async getPhotoshootModelBackgroundPlan(
    product: string,
    variants: number,
    backgroundsCatalogJson: string,
    productImageBase64: string,
    abortSignal?: AbortSignal,
  ): Promise<Array<{ background: string }>> {
    const trimmedProduct = product.trim()
    if (!trimmedProduct) {
      throw new Error('Invalid photoshoot model background plan response structure')
    }
    return this.retryWithBackoff(async () => {
      const response = await this.openAiResponses({
        promptId: 'pmpt_6a51118708648196a1687b3ac580711102f260a552a7eacb',
        version: '7',
        input: [''],
        base64image: productImageBase64,
        variables: {
          product: trimmedProduct,
          backgrounds: backgroundsCatalogJson,
          variants: String(variants),
        },
        abortSignal,
      })

      const parsed = this.parseOpenAiJson(response)
      const shots: Array<{ background?: string }> = parsed?.shots
      if (!Array.isArray(shots) || shots.length === 0) {
        throw new Error('Invalid photoshoot model background plan response structure')
      }
      return shots.slice(0, variants).map((shot) => ({
        background: typeof shot?.background === 'string' ? shot.background.trim() : '',
      }))
    }, abortSignal)
  }

  // Generates a per-image motion prompt for Seedance video generation.
  // Mirrors iOS `EditWithAIAPIManager.prepareVideoGenerationInput` —
  // same OpenAI stored prompt id/version, same `{ prompt }` JSON shape
  // with a raw-text fallback when the response is not JSON.
  async getAIVideoMotionPrompt(
    base64image: string,
    abortSignal?: AbortSignal,
  ): Promise<string> {
    return this.retryWithBackoff(async () => {
      const response = await this.openAiResponses({
        promptId: 'pmpt_69933a9fea008197b8c5f7253b88ee140c9a9ac7c6bed434',
        version: '2',
        base64image,
        abortSignal,
      })

      const raw = typeof response === 'string' ? response : ''
      const trimmedRaw = raw.trim()
      if (!trimmedRaw) throw new Error('Empty AI video prompt response')

      try {
        const parsed = JSON.parse(trimmedRaw)
        const prompt = typeof parsed?.prompt === 'string' ? parsed.prompt.trim() : ''
        if (prompt) return prompt
      } catch {
        // not JSON — use raw text below
      }
      return trimmedRaw
    }, abortSignal)
  }
}

export default new ApiService()
